import React, { useState, useEffect, useCallback } from 'react';
import { MdStar, MdStarBorder, MdStarHalf, MdErrorOutline, MdInfoOutline, MdShowChart, MdPeopleOutline } from 'react-icons/md';
import { getPropertyRatings, getMyProperties } from '../../../api/services/propertyService';
import { getMyAgentAssignments } from '../../../api/services/agentAssignmentsService';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './PropertyRatings.css';

const PropertyRatings = ({ dashboardScope }) => {
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [ratingData, setRatingData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 20;
  const [loading, setLoading] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [error, setError] = useState(null);

  const fetchProperties = useCallback(async () => {
    try {
      setLoadingProperties(true);
      let propertyList = [];
      
      if (dashboardScope === 'AGENT') {
        const response = await getMyAgentAssignments({ page: 0, size: 100 });
        const data = response?.data || response;
        const assignments = data?.content || (Array.isArray(data) ? data : []);
        // Normalize assignment to property-like object
        propertyList = assignments.map(a => ({
          id: a.propertyId,
          name: a.propertyName
        }));
      } else {
        // Normalize dashboardScope to match ManageRentals.jsx behavior
        const normalizedScope = dashboardScope === 'BNB' ? 'BNB' : undefined;
        
        const response = await getMyProperties({ 
          page: 0, 
          size: 100,
          dashboardScope: normalizedScope
        });
        
        const data = response?.data || response;
        propertyList = data?.content || (Array.isArray(data) ? data : []);
      }

      setProperties(propertyList);
      
      if (propertyList.length > 0) {
        setSelectedPropertyId(propertyList[0].id);
      }
    } catch (err) {
      setError('Failed to load properties. Please try again.');
    } finally {
      setLoadingProperties(false);
    }
  }, [dashboardScope]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const fetchRatings = useCallback(async (propertyId, page) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPropertyRatings(propertyId, { 
        page, 
        size: pageSize,
        dashboardScope 
      });
      setRatingData(response.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch ratings.');
    } finally {
      setLoading(false);
    }
  }, [dashboardScope, pageSize]);

  useEffect(() => {
    if (selectedPropertyId) {
      setCurrentPage(0);
      fetchRatings(selectedPropertyId, 0);
    }
  }, [selectedPropertyId, fetchRatings]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchRatings(selectedPropertyId, newPage);
    // Scroll to feedback section
    const feedbackElem = document.querySelector('.feedback-section');
    if (feedbackElem) {
      feedbackElem.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderStars = (score) => {
    const stars = [];
    const fullStars = Math.floor(score);
    const hasHalfStar = score % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<MdStar key={i} />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<MdStarHalf key={i} />);
      } else {
        stars.push(<MdStarBorder key={i} />);
      }
    }
    return stars;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loadingProperties) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading your properties...</p>
      </div>
    );
  }

  if (properties.length === 0 && !loadingProperties) {
    return (
      <div className="empty-state">
        <MdInfoOutline style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '16px' }} />
        <h3 className="ratings-page-title">No Properties Found</h3>
        <p>You don't have any properties yet to see ratings for.</p>
      </div>
    );
  }

  return (
    <div className="ratings-container">
      <CollapsibleTopPanel
        openLabel="Hide filter & intro"
        closedLabel="Show filter & intro"
      >
        <div className="ratings-welcome-banner">
          <h1 className="ratings-welcome-title">Property Ratings</h1>
          <p className="ratings-welcome-subtitle">
            Monitor feedback and ratings from your tenants. High ratings reflect excellence in management.
          </p>
        </div>

        <div className="property-selector-card">
          <label className="property-select-label" htmlFor="property-select">
            VIEW RATINGS FOR:
          </label>
          <select
            id="property-select"
            className="property-select"
            value={selectedPropertyId}
            onChange={(e) => setSelectedPropertyId(e.target.value)}
          >
            {properties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name}
              </option>
            ))}
          </select>
        </div>
      </CollapsibleTopPanel>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Fetching latest ratings...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <MdErrorOutline style={{ fontSize: '48px', color: '#ef4444', marginBottom: '16px' }} />
          <h3>Connection Error</h3>
          <p>{error}</p>
          <button 
            className="landlord-quick-btn" 
            style={{ marginTop: '16px', padding: '10px 24px' }}
            onClick={() => fetchRatings(selectedPropertyId, currentPage)}
          >
            Retry Fetch
          </button>
        </div>
      ) : ratingData ? (
        <>
          <div className="ratings-stats-row">
            <div className="rating-stat-card">
              <div className="stat-icon"><MdShowChart /></div>
              <div className="stat-value-large">{ratingData.averageScore?.toFixed(1) || '0.0'}</div>
              <div className="stars-row">{renderStars(ratingData.averageScore || 0)}</div>
              <div className="stat-label-muted">Average Property Score</div>
            </div>
            <div className="rating-stat-card">
              <div className="stat-icon"><MdPeopleOutline /></div>
              <div className="stat-value-large">{ratingData.ratingCount || 0}</div>
              <div style={{ height: '20px', margin: '8px 0' }}></div>
              <div className="stat-label-muted">Total Verified Reviews</div>
            </div>
          </div>

          <div className="feedback-section">
            <div className="section-header">
              <h2 className="section-title">Tenant Feedback History</h2>
              <span className="stat-label-muted">{ratingData.ratings?.length || 0} entries</span>
            </div>
            
            <div className="feedback-list">
              {ratingData.ratings && ratingData.ratings.length > 0 ? (
                ratingData.ratings.map((rating) => (
                  <div key={rating.id} className="feedback-item">
                    <div className="tenant-avatar">
                      {rating.firstName?.charAt(0)}{rating.lastName?.charAt(0)}
                    </div>
                    <div className="feedback-content">
                      <div className="feedback-top">
                        <div>
                          <h3 className="tenant-name">{rating.firstName} {rating.lastName}</h3>
                          <span className="feedback-date">{formatDate(rating.createdAt)}</span>
                        </div>
                        <div className="feedback-score-tag">
                          <MdStar />
                          <span>{rating.score} / 5</span>
                        </div>
                      </div>
                      <div className="feedback-body">
                        {rating.message ? (
                          <p className="feedback-message">{rating.message}</p>
                        ) : (
                          <p className="feedback-message no-feedback-text">No written feedback provided with this rating.</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <MdInfoOutline style={{ fontSize: '32px', color: '#9ca3af', marginBottom: '12px' }} />
                  <p className="stat-label-muted">No individual reviews have been posted for this property yet.</p>
                </div>
              )}
            </div>

            {ratingData.ratingCount > pageSize && (
              <div className="pagination-container">
                <button
                  className="pagination-btn"
                  disabled={currentPage === 0 || loading}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage + 1} of {Math.ceil(ratingData.ratingCount / pageSize)}
                </span>
                <button
                  className="pagination-btn"
                  disabled={(currentPage + 1) * pageSize >= ratingData.ratingCount || loading}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default PropertyRatings;
